import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import {
  CURRENT_EULA_EFFECTIVE_DATE,
  CURRENT_EULA_VERSION,
} from '@/lib/eula';
import type { Database } from '@/types/database';

export type CurrentAcceptance =
  Database['public']['Views']['eula_acceptances_current']['Row'];

const ACCEPTANCE_KEY = (userId: string | null | undefined) =>
  ['eula', 'current-acceptance', userId ?? 'anon'] as const;

/**
 * Returns the current authenticated user's most recent (non-withdrawn) EULA
 * acceptance, or null if they have never accepted. Reads from the
 * eula_acceptances_current view; RLS ensures the user only sees their own row.
 *
 * Pass the authenticated user's id from useAuth() so the cache key partitions
 * correctly across sign-in/sign-out — without it, an old user's acceptance
 * could be served to a freshly signed-in user from cache.
 */
export function useCurrentEulaAcceptance(userId: string | null | undefined) {
  return useQuery({
    queryKey: ACCEPTANCE_KEY(userId),
    enabled: !!userId,
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from('eula_acceptances_current')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) {
        // If the view doesn't exist yet (migration not applied) treat it as
        // "no acceptance" rather than crashing the gate. The user will see
        // the click-through page; they just won't have a stale-version
        // bypass until migrations are run.
        console.error('[useCurrentEulaAcceptance] failed', error);
        return null;
      }
      return (data ?? null) as CurrentAcceptance | null;
    },
    staleTime: 60_000,
  });
}

/**
 * Convenience selector — `true` when the user has actively accepted the
 * version of the EULA currently advertised in src/lib/eula.ts.
 */
export function isCurrentVersionAccepted(
  acceptance: CurrentAcceptance | null | undefined,
): boolean {
  if (!acceptance) return false;
  return acceptance.eula_version === CURRENT_EULA_VERSION;
}

/**
 * Records the current authenticated user's acceptance of the current EULA
 * version via the `eula-accept` Edge Function. Server-side captures IP and
 * user-agent; we just supply the version + effective date the user saw and
 * any client-derived metadata (scroll depth, locale).
 */
export function useAcceptEula() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      documentHash?: string;
      metadata?: Record<string, unknown>;
    }) => {
      return invokeEdgeFunction<{
        ok: true;
        id: string;
        accepted_at: string;
        deduped?: boolean;
      }>('eula-accept', {
        eula_version: CURRENT_EULA_VERSION,
        effective_date: CURRENT_EULA_EFFECTIVE_DATE,
        document_hash: input.documentHash,
        metadata: input.metadata ?? {},
      });
    },
    onSuccess: () => {
      // Invalidate every variant of the acceptance query so the gate
      // re-checks on the next render and lets the user through. We RETURN
      // the invalidation promise here so React Query waits for the refetch
      // before mutateAsync resolves — otherwise EulaAcceptPage would
      // navigate while the query cache still held the old (null) value,
      // and RequireEulaAcceptance would briefly bounce the user back here
      // before the refetch completed.
      return qc.invalidateQueries({ queryKey: ['eula'] });
    },
  });
}

async function invokeEdgeFunction<T>(
  name: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    const message = await extractFunctionErrorMessage(error);
    throw new Error(message);
  }
  return data as T;
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback =
    error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as FunctionsHttpError).context as Response | undefined;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const cloned = ctx.clone();
    const body = (await cloned.json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}
